import React, { useEffect, useState } from "react";
import { Alert, Box } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { isAdmin } from "utils/roles";

// Global banner that warns admins when the Meta FB_ACCESS_TOKEN data-access
// window is closing (<=10 days) or already expired. Renders on every page
// inside Layout, fetches once per Layout mount, hides itself once the token
// is rotated and the status refreshes (Settings page has a manual Refresh).
function FbTokenExpiryBanner() {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!isAdmin(user) || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}systemServer/fb-token-status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setStatus(data);
      } catch {
        // Silent — admin can still see the full card in Settings.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, token]);

  if (!isAdmin(user)) return null;
  if (!status?.hasData) return null;

  const days = status.daysRemaining;
  const expired = days !== null && days < 0;
  const critical = days !== null && days >= 0 && days <= 10;
  if (!expired && !critical) return null;

  const dayWord = (n) => (n === 1 ? "day" : "days");
  const severity = expired ? "error" : "warning";
  const message = expired
    ? `Meta access token expired ${Math.abs(days)} ${dayWord(Math.abs(days))} ago — re-issue FB_ACCESS_TOKEN now.`
    : `Meta access token expires in ${days} ${dayWord(days)}. Rotate it before it dies.`;

  return (
    <Box sx={{ px: 1.5, pt: 1 }}>
      <Alert
        severity={severity}
        sx={{ borderRadius: 1 }}
        action={
          <RouterLink
            to="/settings?tab=system"
            style={{
              color: "inherit",
              fontWeight: 600,
              textDecoration: "underline",
              marginRight: 8,
            }}
          >
            Fix in Settings
          </RouterLink>
        }
      >
        {message}
      </Alert>
    </Box>
  );
}

export default FbTokenExpiryBanner;
